import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowRightLeft } from 'lucide-react';
import { ConfirmActionDialog } from './ConfirmActionDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v, d = 2) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: d });

export const MarketSwapForm = ({ token, state, busy, onStarted }) => {
  const [amount, setAmount] = useState('');
  const [slippage, setSlippage] = useState('5');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const poolReady = state?.pool?.exists && state.pool.liquidity > 0;

  const getQuote = async () => {
    setLoading(true); setQuote(null);
    try {
      const r = await axios.get(`${API}/pas-admin/market/swap-quote`, { params: { amount_beth: amount, slippage_pct: slippage }, headers });
      setQuote(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not get a quote'); } finally { setLoading(false); }
  };

  const swap = async () => {
    setConfirmOpen(false);
    try {
      const r = await axios.post(`${API}/pas-admin/market/swap`, { amount_beth: parseFloat(amount), slippage_pct: parseFloat(slippage), confirm: true }, { headers });
      onStarted(r.data.job_id); setQuote(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Swap failed to start'); }
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-5 space-y-4" data-testid="market-swap-form">
      <h4 className="text-white font-semibold flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-purple-400" /> Swap BETH → USDC</h4>
      {!poolReady && <p className="text-xs text-amber-300" data-testid="market-swap-pool-empty">The BETH/USDC pool has no funds yet, so add funds to the pool first.</p>}
      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm text-slate-300 space-y-1">BETH amount
          <input type="number" min="0" value={amount} onChange={(e) => { setAmount(e.target.value); setQuote(null); }} data-testid="market-swap-amount-input"
            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white font-mono" />
        </label>
        <label className="text-sm text-slate-300 space-y-1">Stop if price moves more than (%)
          <input type="number" min="0.1" max="50" step="0.5" value={slippage} onChange={(e) => { setSlippage(e.target.value); setQuote(null); }} data-testid="market-swap-slippage-input"
            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white font-mono" />
        </label>
      </div>
      <p className="text-xs text-slate-500">Wallet holds {fmt(state?.wallet?.beth)} BETH</p>
      {quote && (
        <div className="text-sm space-y-1 rounded bg-slate-800/70 p-3" data-testid="market-swap-quote">
          <p className="text-white">You get about <strong>{fmt(quote.usdc_out, 6)} USDC</strong> (at least {fmt(quote.min_usdc_out, 6)})</p>
          <p className="text-slate-400 text-xs">Price {fmt(quote.exec_price, 6)} USDC/BETH · pool price {fmt(quote.spot_price, 6)} · price impact {quote.price_impact_pct}% · network fee ≈ {fmt(quote.network_fee_eth, 6)} ETH</p>
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={getQuote} disabled={!poolReady || !(amount > 0) || loading} data-testid="market-swap-quote-btn"
          className="px-4 py-2 text-sm rounded bg-slate-700 hover:bg-slate-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white flex items-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Get quote
        </button>
        <button onClick={() => setConfirmOpen(true)} disabled={!quote || busy} data-testid="market-swap-submit-btn"
          className="px-4 py-2 text-sm rounded bg-purple-500 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white">Swap</button>
      </div>
      <ConfirmActionDialog open={confirmOpen} testId="market-swap-confirm" title="Swap BETH for USDC" confirmLabel="Confirm swap"
        onCancel={() => setConfirmOpen(false)} onConfirm={swap}
        rows={quote ? [['Sell', `${fmt(quote.amount_beth, 4)} BETH`], ['Expected', `${fmt(quote.usdc_out, 6)} USDC`],
          ['Minimum (else cancelled)', `${fmt(quote.min_usdc_out, 6)} USDC`], ['Price impact', `${quote.price_impact_pct}%`]] : []}
        warning={quote?.price_impact_pct > 10 ? 'High price impact: this sale moves the price a lot.' : null} />
    </div>
  );
};

export default MarketSwapForm;
